import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { Briefcase, ShieldCheck, ShieldAlert, Star, Banknote, MapPin, MessageSquare, Flag, FileCheck2, FileX2, AlertCircle, ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import RoyalReferralCard from "@/components/RoyalReferralCard";
import RoyalEmpowermentPanel from "@/components/RoyalEmpowermentPanel";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import NegotiableBidsInbox from "@/components/NegotiableBidsInbox";
import LiveJobsFeed from "@/components/LiveJobsFeed";
import RateCustomerDialog from "@/components/RateCustomerDialog";
import CustomerReviewPopover from "@/components/CustomerReviewPopover";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const JM_BANKS = ["NCB", "Scotiabank", "JN Bank", "Sagicor Bank", "First Global", "CIBC FirstCaribbean", "VMBS", "Other"];

export default function ProviderDashboard() {
    const { user } = useAuth();
    const [provider, setProvider] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [balance, setBalance] = useState(null);
    const [empowerment, setEmpowerment] = useState(null);
    const [appStatus, setAppStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rateCustomerBooking, setRateCustomerBooking] = useState(null);
    const [reputationCustomer, setReputationCustomer] = useState(null);

    const refresh = () => {
        Promise.all([
            api.get("/providers/me").then((r) => setProvider(r.data)).catch(() => setProvider(null)),
            api.get("/providers/me/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([])),
            api.get("/providers/me/balance").then((r) => setBalance(r.data)).catch(() => setBalance(null)),
            api.get("/providers/me/royal-empowerment").then((r) => setEmpowerment(r.data)).catch(() => setEmpowerment(null)),
            api.get("/providers/me/application-status").then((r) => setAppStatus(r.data)).catch(() => setAppStatus(null)),
        ]).finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
    }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/bookings/${id}/status`, { status });
            toast.success(`Job marked ${status.replace("_", " ")}`);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const earnings = jobs
        .filter((j) => j.status === "completed" && j.payment_status === "paid")
        .reduce((sum, j) => sum + j.total_amount, 0);

    return (
        <div className="min-h-screen bg-background" data-testid="provider-dashboard">
            <Header />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <div className="overline mb-3">Provider Console</div>
                <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter">
                    Welcome back,<br />
                    <span className="italic gold-grad">{user?.name?.split(" ")[0]}</span>.
                </h1>

                {appStatus && appStatus.status !== "approved" && (
                    <ApplicationStatusBanner status={appStatus} />
                )}

                {/* ROYAL EMPOWERMENT — Wallet · Tiers · Instant Pay */}
                {empowerment && provider && (
                    <div className="mt-10">
                        <RoyalEmpowermentPanel
                            data={empowerment}
                            onRequestInstantPay={async () => {
                                try {
                                    await api.post("/providers/me/instant-pay");
                                    toast.success("Instant Pay request submitted. We'll process it within 30 minutes.");
                                    refresh();
                                } catch (e) {
                                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                                }
                            }}
                        />
                    </div>
                )}

                {/* DEMAND NOTIFICATIONS — Web Push opt-in */}
                {provider && provider.application_status === "approved" && (
                    <div className="mt-6">
                        <PushNotificationToggle
                            serviceCategories={provider.service_categories || []}
                        />
                    </div>
                )}

                {!loading && !provider && !appStatus && (
                    <div className="mt-12 p-10 bg-card border border-white/10">
                        <h3 className="font-serif text-2xl mb-3">Complete your application</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            You haven't submitted a provider profile yet.
                        </p>
                        <Button asChild className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7">
                            <a href="/become-provider" data-testid="provider-apply-link">Apply Now</a>
                        </Button>
                    </div>
                )}

                {provider && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 mt-12 mb-12" data-testid="provider-stats">
                            <Stat
                                label="Status"
                                value={
                                    provider.verified ? (
                                        <span className="flex items-center gap-2 gold-text">
                                            <ShieldCheck size={18} strokeWidth={1.5} /> Verified
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2 text-amber-300">
                                            <ShieldAlert size={18} strokeWidth={1.5} /> Pending
                                        </span>
                                    )
                                }
                            />
                            <Stat label="Total Jobs" value={provider.total_jobs} />
                            <Stat
                                label="Rating"
                                value={
                                    <span className="flex items-center gap-2">
                                        <Star size={16} strokeWidth={1.5} className="gold-text" />
                                        {provider.rating?.toFixed(1) || "—"}
                                    </span>
                                }
                            />
                            <Stat
                                label="Earnings (paid)"
                                value={
                                    <>
                                        <span className="gold-text">$</span>
                                        {earnings.toLocaleString()}
                                        <span className="text-xs text-muted-foreground ml-1">JMD</span>
                                    </>
                                }
                            />
                        </div>

                        <BankAndPayouts provider={provider} balance={balance} onSaved={refresh} />

                        <BaseLocation provider={provider} onSaved={refresh} />

                        <div className="overline mb-6">Incoming & Active Jobs</div>{jobs.length === 0 ? (
                            <div className="p-12 bg-card border border-white/5 text-center text-muted-foreground" data-testid="empty-jobs">
                                <Briefcase size={32} strokeWidth={1} className="gold-text mx-auto mb-4" />
                                No jobs yet. Once a customer books your service, it'll appear here.
                            </div>
                        ) : (
                            <div className="space-y-4" data-testid="provider-jobs-list">
                                {jobs.map((j) => (
                                    <article
                                        key={j.id}
                                        className="grid lg:grid-cols-12 gap-6 p-6 lg:p-8 bg-card border border-white/5"
                                        data-testid={`provider-job-${j.id}`}
                                    >
                                        <div className="lg:col-span-5">
                                            <div className="overline mb-2">{j.service_category}</div>
                                            <h3 className="font-serif text-xl">{j.service_name}</h3>
                                            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                                                <span>Customer · {j.customer_name}</span>
                                                {j.customer_rating_count > 0 ? (
                                                    <button
                                                        data-testid={`customer-reputation-${j.id}`}
                                                        onClick={() => setReputationCustomer({ id: j.customer_id, name: j.customer_name })}
                                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] uppercase tracking-[0.22em] cursor-pointer hover:scale-105 transition-transform ${
                                                            j.customer_rating_avg >= 4.5
                                                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                                                                : j.customer_rating_avg >= 3.5
                                                                ? "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                                                                : "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                                                        }`}
                                                        title={`${j.customer_rating_count} rating${j.customer_rating_count === 1 ? "" : "s"} from pros — tap to see them`}
                                                    >
                                                        <Star size={9} fill="currentColor" />
                                                        {j.customer_rating_avg.toFixed(1)}
                                                        <span className="normal-case tracking-normal opacity-70">
                                                            ({j.customer_rating_count})
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <button
                                                        data-testid={`customer-reputation-new-${j.id}`}
                                                        onClick={() => setReputationCustomer({ id: j.customer_id, name: j.customer_name })}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-muted-foreground text-[9px] uppercase tracking-[0.22em] cursor-pointer hover:bg-white/10 transition-colors"
                                                        title="First-time or unrated customer"
                                                    >
                                                        New customer
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="lg:col-span-3 text-sm text-muted-foreground space-y-1">
                                            <div>{format(parseISO(j.scheduled_at), "MMM d · HH:mm")}</div>
                                            <div>{j.city}</div>
                                            <div className="text-xs truncate">{j.address}</div>
                                        </div>
                                        <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-3">
                                            <Badge className="uppercase text-[10px] tracking-[0.2em] rounded-sm bg-secondary text-foreground border-white/10">
                                                {j.status.replace("_", " ")}
                                            </Badge>
                                            <div className="flex flex-wrap gap-2">
                                                {j.status === "pending" && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => updateStatus(j.id, "confirmed")}
                                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-primary text-primary-foreground"
                                                        data-testid={`accept-${j.id}`}
                                                    >
                                                        Accept
                                                    </Button>
                                                )}
                                                {j.status === "confirmed" && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => updateStatus(j.id, "in_progress")}
                                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-primary text-primary-foreground"
                                                        data-testid={`start-${j.id}`}
                                                    >
                                                        Start
                                                    </Button>
                                                )}
                                                {j.status === "in_progress" && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => updateStatus(j.id, "completed")}
                                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-primary text-primary-foreground"
                                                        data-testid={`complete-${j.id}`}
                                                    >
                                                        Complete
                                                    </Button>
                                                )}
                                                {j.status === "completed" && !j.customer_rating && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setRateCustomerBooking(j)}
                                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-[#FFD700] text-black hover:bg-amber-300"
                                                        data-testid={`rate-customer-${j.id}`}
                                                    >
                                                        <Star size={12} className="mr-1" fill="currentColor" /> Rate Customer
                                                    </Button>
                                                )}
                                                {j.status === "completed" && j.customer_rating && (
                                                    <span
                                                        className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 inline-flex items-center gap-1"
                                                        data-testid={`rated-customer-${j.id}`}
                                                    >
                                                        <Star size={10} fill="currentColor" /> Rated {j.customer_rating}/5
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}

                        <ProviderReviews provider={provider} jobs={jobs} onChanged={refresh} />

                        {/* Live Jobs Feed — ⚡ one-tap claim for every open fixed-price job
                            in the pro's category + city.  Auto-refreshes every 15s. */}
                        <div className="mt-10">
                            <LiveJobsFeed provider={provider} />
                        </div>

                        {/* Negotiable bids inbox — Handyman trades & any future is_negotiable service */}
                        <div className="mt-10">
                            <NegotiableBidsInbox provider={provider} />
                        </div>

                        {/* Royal Referrals — pros earn SMART Coins for every Royal Pro they bring */}
                        <div className="mt-10">
                            <RoyalReferralCard />
                        </div>
                    </>
                )}
            </section>

            <RateCustomerDialog
                booking={rateCustomerBooking}
                open={!!rateCustomerBooking}
                onClose={() => setRateCustomerBooking(null)}
                onDone={refresh}
            />

            {reputationCustomer && (
                <CustomerReviewPopover
                    customerId={reputationCustomer.id}
                    customerName={reputationCustomer.name}
                    onClose={() => setReputationCustomer(null)}
                />
            )}

            <Footer />
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-background p-6 lg:p-8">
            <div className="overline mb-3">{label}</div>
            <div className="font-serif text-2xl lg:text-3xl">{value}</div>
        </div>
    );
}

function BankAndPayouts({ provider, balance, onSaved }) {
    const existing = provider?.bank_details || {};
    const [bankName, setBankName] = useState(existing.bank_name || "NCB");
    const [accountHolder, setAccountHolder] = useState(existing.account_holder || provider?.name || "");
    const [accountNumber, setAccountNumber] = useState(existing.account_number || "");
    const [branch, setBranch] = useState(existing.branch || "");
    const [accountType, setAccountType] = useState(existing.account_type || "savings");
    const [editing, setEditing] = useState(!provider?.bank_details);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!accountHolder || !accountNumber) {
            toast.error("Account holder and number are required");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/providers/me/bank", {
                bank_name: bankName,
                account_holder: accountHolder,
                account_number: accountNumber,
                branch: branch || null,
                account_type: accountType,
            });
            toast.success("Bank details saved");
            setEditing(false);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mb-12 grid lg:grid-cols-3 gap-6" data-testid="provider-payouts-block">
            <div className="lg:col-span-2 p-6 bg-card border border-white/10 rounded-sm">
                <div className="flex items-center gap-2 mb-4">
                    <Banknote size={16} className="gold-text" strokeWidth={1.5} />
                    <h3 className="font-serif text-2xl">Bank for payouts (JMD)</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-5">
                    SMART pays providers via weekly bank transfer. Make sure your details are correct — payouts are released every Friday.
                </p>
                {!editing && provider?.bank_details ? (
                    <div className="space-y-2 text-sm">
                        <Row k="Bank" v={existing.bank_name} />
                        <Row k="Account holder" v={existing.account_holder} />
                        <Row k="Account number" v={existing.account_number} />
                        <Row k="Branch" v={existing.branch || "—"} />
                        <Row k="Type" v={existing.account_type} />
                        <Button
                            onClick={() => setEditing(true)}
                            className="mt-3 bg-secondary text-foreground hover:bg-white/10 rounded-sm uppercase tracking-[0.2em] text-xs h-9"
                            data-testid="provider-bank-edit-btn"
                        >
                            Edit details
                        </Button>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="provider-bank-form">
                        <Field label="Bank">
                            <select value={bankName} onChange={(e) => setBankName(e.target.value)} className="bg-input border border-white/10 h-11 rounded-sm w-full px-3 text-sm" data-testid="provider-bank-name-select">
                                {JM_BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </Field>
                        <Field label="Account type">
                            <select value={accountType} onChange={(e) => setAccountType(e.target.value)} className="bg-input border border-white/10 h-11 rounded-sm w-full px-3 text-sm" data-testid="provider-bank-type-select">
                                <option value="savings">Savings</option>
                                <option value="chequing">Chequing</option>
                            </select>
                        </Field>
                        <Field label="Account holder *">
                            <Input value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="provider-bank-holder-input" />
                        </Field>
                        <Field label="Account number *">
                            <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} required className="bg-input border-white/10 h-11 rounded-sm" data-testid="provider-bank-number-input" />
                        </Field>
                        <Field label="Branch (optional)">
                            <Input value={branch} onChange={(e) => setBranch(e.target.value)} className="bg-input border-white/10 h-11 rounded-sm" data-testid="provider-bank-branch-input" />
                        </Field>
                        <div className="flex items-end">
                            <Button
                                onClick={save}
                                disabled={saving}
                                className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11 px-6"
                                data-testid="provider-bank-save-btn"
                            >
                                {saving ? "Saving..." : "Save bank details"}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-6 bg-primary/10 border border-primary/40 rounded-sm" data-testid="provider-balance-card">
                <div className="overline">Pending payout</div>
                <div className="font-serif text-4xl gold-text mt-3">JMD ${Math.round(balance?.pending || 0).toLocaleString()}</div>
                <div className="mt-4 space-y-1 text-xs text-muted-foreground">
                    <div>Earned (lifetime): <span className="text-foreground">${Math.round(balance?.earned || 0).toLocaleString()}</span></div>
                    <div>Paid out: <span className="text-foreground">${Math.round(balance?.paid_out || 0).toLocaleString()}</span></div>
                    <div>Completed jobs: <span className="text-foreground">{balance?.completed_jobs || 0}</span></div>
                </div>
                {balance?.last_payout && (
                    <div className="mt-4 text-[11px] text-muted-foreground border-t border-white/10 pt-3">
                        Last paid: {new Date(balance.last_payout.paid_at).toLocaleDateString()} · ${Math.round(balance.last_payout.amount).toLocaleString()}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            {children}
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between gap-4 text-xs">
            <span className="text-muted-foreground uppercase tracking-[0.2em]">{k}</span>
            <span className="text-foreground">{v}</span>
        </div>
    );
}

function BaseLocation({ provider, onSaved }) {
    const [address, setAddress] = useState(provider?.base_address || "");
    const [city, setCity] = useState(provider?.base_city || "Kingston");
    const [saving, setSaving] = useState(false);
    const hasLoc = provider?.base_lat != null && provider?.base_lng != null;

    const save = async () => {
        if (!address.trim()) {
            toast.error("Enter an address");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/providers/me/base-location", { address, city });
            toast.success("Base location saved — clients will see your distance & ETA");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mb-12 p-6 bg-card border border-white/10 rounded-sm" data-testid="provider-base-location">
            <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="gold-text" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl">Your base location</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-5">
                Where you start your day. We use this to show clients distance + ETA, and rank you on "Nearest pros" lists. Approximate is fine — only the rough lat/lng is shown publicly.
            </p>
            {hasLoc && (
                <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-700/40 text-emerald-300 rounded-sm text-[10px] uppercase tracking-[0.2em]">
                    <ShieldCheck size={12} strokeWidth={1.5} /> Location set · {provider.base_lat?.toFixed(3)}, {provider.base_lng?.toFixed(3)}
                </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Street / area">
                    <Input
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="e.g. Cross Roads"
                        className="bg-input border-white/10 h-11 rounded-sm"
                        data-testid="provider-base-address-input"
                    />
                </Field>
                <Field label="City">
                    <Input
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Kingston"
                        className="bg-input border-white/10 h-11 rounded-sm"
                        data-testid="provider-base-city-input"
                    />
                </Field>
                <div className="flex items-end">
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                        data-testid="provider-base-save-btn"
                    >
                        {saving ? "Saving…" : hasLoc ? "Update" : "Save location"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ProviderReviews({ provider, jobs, onChanged }) {
    // Filter jobs that have a customer review attached
    const reviewed = jobs.filter((j) => j.rating && j.review_comment !== undefined);
    if (reviewed.length === 0) {
        return null;
    }
    const avg = reviewed.reduce((s, j) => s + j.rating, 0) / reviewed.length;
    return (
        <div className="mt-16" data-testid="provider-reviews-section">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="overline mb-2">Customer feedback</div>
                    <h2 className="font-serif text-3xl">Your reviews</h2>
                </div>
                <div className="text-right">
                    <div className="font-serif text-3xl gold-text inline-flex items-center gap-2">
                        <Star size={20} className="fill-current" /> {avg.toFixed(1)}
                    </div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-[0.2em] mt-1">
                        {reviewed.length} review{reviewed.length === 1 ? "" : "s"}
                    </div>
                </div>
            </div>
            <div className="space-y-4">
                {reviewed.map((j) => (
                    <ReviewCard key={j.id} job={j} onReplied={onChanged} />
                ))}
            </div>
        </div>
    );
}

function ReviewCard({ job, onReplied }) {
    const [reply, setReply] = useState("");
    const [posting, setPosting] = useState(false);
    const [reported, setReported] = useState(!!job.review_report_count);
    const [reportOpen, setReportOpen] = useState(false);
    const [reportReason, setReportReason] = useState("");
    const [reporting, setReporting] = useState(false);
    const hasReply = !!job.provider_reply;

    const submit = async () => {
        if (reply.trim().length < 1) {
            toast.error("Type a reply first");
            return;
        }
        setPosting(true);
        try {
            await api.post(`/bookings/${job.id}/reply`, { reply });
            toast.success("Reply published");
            setReply("");
            onReplied?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setPosting(false);
        }
    };

    const submitReport = async () => {
        if (reportReason.trim().length < 3) {
            toast.error("Tell us why you're reporting this review");
            return;
        }
        setReporting(true);
        try {
            await api.post(`/bookings/${job.id}/report`, { reason: reportReason });
            toast.success("Reported — our team will review it shortly");
            setReportOpen(false);
            setReportReason("");
            setReported(true);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setReporting(false);
        }
    };

    return (
        <article
            className="p-6 bg-card border border-white/5 rounded-sm"
            data-testid={`provider-review-${job.id}`}
        >
            <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                    <div className="overline mb-1">{job.service_name}</div>
                    <div className="text-xs text-muted-foreground">
                        {job.customer_name}{job.reviewed_at ? ` · ${format(parseISO(job.reviewed_at), "MMM d, yyyy")}` : ""}
                    </div>
                </div>
                <div className="flex items-center gap-1 gold-text">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                            key={`pro-star-${job.id}-${n}`}
                            size={14}
                            className={n <= job.rating ? "fill-current" : "opacity-30"}
                            strokeWidth={1.5}
                        />
                    ))}
                </div>
            </div>
            {job.review_comment && (
                <p className="text-sm text-foreground/90 mb-4 leading-relaxed">{job.review_comment}</p>
            )}

            {hasReply ? (
                <div className="mt-4 pl-4 border-l-2 border-primary/40 bg-primary/5 p-4 rounded-sm">
                    <div className="overline mb-2 inline-flex items-center gap-2">
                        <MessageSquare size={12} strokeWidth={1.5} /> Your reply
                    </div>
                    <p className="text-sm text-foreground/90">{job.provider_reply.text}</p>
                </div>
            ) : (
                <div className="mt-3 pt-4 border-t border-white/5">
                    <Label className="overline mb-2 block">Reply (one-time, public)</Label>
                    <Textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Thank the customer or clarify the situation. Phone numbers will be auto-redacted."
                        className="bg-input border-white/10 rounded-sm min-h-[80px] text-sm mb-3"
                        maxLength={1500}
                        data-testid={`reply-input-${job.id}`}
                    />
                    <Button
                        onClick={submit}
                        disabled={posting || reply.trim().length === 0}
                        size="sm"
                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-primary text-primary-foreground"
                        data-testid={`reply-submit-${job.id}`}
                    >
                        {posting ? "Posting..." : "Publish reply"}
                    </Button>
                </div>
            )}

            <div className="mt-4 pt-3 border-t border-white/5 text-right">
                <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                    <DialogTrigger asChild>
                        <button
                            type="button"
                            disabled={reported}
                            className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`report-btn-${job.id}`}
                        >
                            <Flag size={11} strokeWidth={1.5} />
                            {reported ? "Reported" : "Report this review"}
                        </button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-white/10" data-testid="report-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl">Report review</DialogTitle>
                            <DialogDescription className="text-sm text-muted-foreground">
                                Our moderators will review this within 24 hours. Reviews that violate our guidelines (abuse, hate speech, false claims) will be removed.
                            </DialogDescription>
                        </DialogHeader>
                        <Textarea
                            value={reportReason}
                            onChange={(e) => setReportReason(e.target.value)}
                            placeholder="Why are you reporting this review? (e.g. abusive language, false accusation, off-topic)"
                            className="bg-input border-white/10 rounded-sm min-h-[100px]"
                            maxLength={500}
                            data-testid="report-reason-input"
                        />
                        <DialogFooter>
                            <Button
                                onClick={() => setReportOpen(false)}
                                className="bg-secondary text-foreground hover:bg-white/10 rounded-sm uppercase tracking-[0.2em] text-xs h-10"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={submitReport}
                                disabled={reporting || reportReason.trim().length < 3}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-sm uppercase tracking-[0.2em] text-xs h-10"
                                data-testid="report-submit-btn"
                            >
                                {reporting ? "Reporting..." : "Submit report"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </article>
    );
}


function ApplicationStatusBanner({ status }) {
    // Style maps tied to the colour string returned by the API.
    const PALETTES = {
        muted: { bg: "bg-card", border: "border-white/10", accent: "text-muted-foreground", icon: AlertCircle },
        amber: { bg: "bg-amber-500/5", border: "border-amber-500/30", accent: "text-amber-300", icon: AlertCircle },
        red: { bg: "bg-rose-500/5", border: "border-rose-500/40", accent: "text-rose-300", icon: AlertCircle },
        green: { bg: "bg-emerald-500/5", border: "border-emerald-500/40", accent: "text-emerald-300", icon: ShieldCheck },
    };
    const p = PALETTES[status.color] || PALETTES.muted;
    const Icon = p.icon;
    const ctaLabel = status.application_submitted ? "Finish Application" : "Start Application";

    // GRADUATED VETTING — verification chip + grace-period countdown
    const level = status.verification_level || "none";
    const days = status.days_remaining;
    const isProvisional = level === "provisional";
    const isRoyal = level === "royal";
    const isSuspended = level === "suspended";
    const urgent = isProvisional && days !== null && days <= 5;
    const levelStyles = {
        provisional: "bg-amber-500/10 text-amber-300 border-amber-500/40",
        royal: "bg-primary/15 gold-text border-primary/50",
        suspended: "bg-rose-500/15 text-rose-200 border-rose-500/50",
        none: "bg-card text-muted-foreground border-white/10",
    };

    return (
        <div
            className={`mt-10 lg:mt-12 p-7 lg:p-9 ${p.bg} border ${p.border} rounded-sm`}
            data-testid="application-status-banner"
        >
            {/* Verification level chip + grace-period countdown */}
            {level !== "none" && (
                <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="verification-level-chip">
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-sm uppercase tracking-[0.2em] text-[10px] font-semibold ${levelStyles[level]}`}>
                        {isRoyal && <ShieldCheck size={12} strokeWidth={2} />}
                        {isProvisional && <Clock size={12} strokeWidth={2} />}
                        {isSuspended && <AlertCircle size={12} strokeWidth={2} />}
                        {status.verification_label}
                    </span>
                    {isProvisional && days !== null && (
                        <span
                            data-testid="grace-period-countdown"
                            className={`text-[11px] uppercase tracking-[0.2em] ${urgent ? "text-rose-300 font-semibold" : "text-muted-foreground"}`}
                        >
                            {days >= 0
                                ? `${days} day${days === 1 ? "" : "s"} to upload Police Cert + Trade Licence`
                                : "Grace period ended — upload hard docs to reactivate"}
                        </span>
                    )}
                    {isSuspended && (
                        <span className="text-[11px] uppercase tracking-[0.2em] text-rose-300 font-semibold">
                            Bookings paused — upload your hard docs to reactivate
                        </span>
                    )}
                </div>
            )}

            <div className="flex items-start gap-4 mb-6">
                <Icon size={22} className={`${p.accent} mt-1 shrink-0`} strokeWidth={1.5} />
                <div className="flex-1">
                    <div className={`overline mb-1 ${p.accent}`}>Application Status</div>
                    <h3 className="font-serif text-2xl sm:text-3xl">{status.label}</h3>
                    {status.admin_notes && (
                        <p className="mt-3 text-sm text-foreground/90 italic">
                            "{status.admin_notes}" <span className="text-muted-foreground">— SMART team</span>
                        </p>
                    )}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-2 mb-6" data-testid="application-doc-checklist">
                {status.documents.map((doc) => {
                    const isHard = doc.tier === "hard";
                    const missingCritical = !doc.uploaded && doc.required && !isHard;
                    const missingGrace = !doc.uploaded && isHard;
                    return (
                    <div
                        key={doc.key}
                        className={`flex items-center justify-between gap-3 px-4 py-3 rounded-sm border ${
                            doc.uploaded
                                ? "bg-emerald-500/5 border-emerald-500/30"
                                : missingCritical
                                    ? "bg-rose-500/5 border-rose-500/30"
                                    : missingGrace
                                        ? "bg-amber-500/5 border-amber-500/30"
                                        : "bg-card border-white/10"
                        }`}
                        data-testid={`doc-${doc.key}`}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            {doc.uploaded ? (
                                <FileCheck2 size={16} strokeWidth={1.5} className="text-emerald-300 shrink-0" />
                            ) : (
                                <FileX2
                                    size={16}
                                    strokeWidth={1.5}
                                    className={`shrink-0 ${missingCritical ? "text-rose-300" : missingGrace ? "text-amber-300" : "text-muted-foreground"}`}
                                />
                            )}
                            <span className="text-sm truncate">{doc.label}</span>
                            {isHard && !doc.uploaded && (
                                <span className="text-[9px] uppercase tracking-[0.25em] text-amber-300/80 shrink-0">90-day grace</span>
                            )}
                            {!doc.required && (
                                <span className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground/70">Optional</span>
                            )}
                        </div>
                        <span
                            className={`text-[10px] uppercase tracking-[0.25em] shrink-0 ${
                                doc.uploaded
                                    ? "text-emerald-300"
                                    : missingCritical
                                        ? "text-rose-300 font-semibold"
                                        : missingGrace
                                            ? "text-amber-300"
                                            : "text-muted-foreground"
                            }`}
                        >
                            {doc.uploaded ? "Uploaded" : missingCritical ? "Missing" : missingGrace ? "Pending" : "—"}
                        </span>
                    </div>
                    );
                })}
            </div>

            {status.status !== "approved" && status.status !== "rejected" && (
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7">
                    <Link to="/become-provider" data-testid="finish-application-btn">
                        {ctaLabel} <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                    </Link>
                </Button>
            )}
        </div>
    );
}
